import {
  AuditOutlined,
  CloudDownloadOutlined,
  CloudUploadOutlined,
  DashboardOutlined,
  DatabaseOutlined,
  DesktopOutlined,
  ExperimentOutlined,
  FileDoneOutlined,
  IdcardOutlined,
  MedicineBoxOutlined,
  NotificationOutlined,
  PlusCircleOutlined,
  ProfileOutlined,
  SearchOutlined,
  SendOutlined,
  SettingOutlined,
  SwapOutlined,
  TeamOutlined,
  ToolOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { css } from '@emotion/css';
import { ConfigProvider, Menu, theme } from 'antd';
import type { ItemType } from 'antd/es/menu/interface';
import type React from 'react';
import { useAuth } from 'react-oidc-context';
import { Link, useLocation } from 'react-router';
import { useLaunchOnly } from '../ais/useAisCapabilities';
import { useOpenRequestsCount } from '../hooks/useOpenRequestsCount';
import { hasFrontDeskRole, hasSomeRole, Role } from '../utils/user';
import { AiSparklesIcon } from './icons/AiSparklesIcon.tsx';

const { useToken } = theme;

// fallow-ignore-next-line complexity
export const MainMenu: React.FC<{ aisSession: boolean }> = ({ aisSession }) => {
  const { token } = useToken();
  const auth = useAuth();
  const location = useLocation();
  const levels = location.pathname.split('/');
  const level1 = levels[1];
  const level2 = levels[2];
  const hasSubmenuItemsWithoutSubmenu = level1 === 'anforderung' || level1 === 'zuweiserauswahl' || level1 === 'labadmin';
  const isFrontDesk = hasFrontDeskRole(auth.user);
  const openCount = useOpenRequestsCount();
  const launchOnly = useLaunchOnly();

  // Prominent primary action — kept as a menu item so it collapses natively with the Sider.
  const neueAnforderung: ItemType = {
    key: 'anforderung',
    className: 'menu-cta',
    icon: <PlusCircleOutlined />,
    label: <Link to="/anforderung">Neue Anforderung</Link>,
  };

  const patientSuche: ItemType = {
    key: 'patient-suche',
    icon: <SearchOutlined />,
    label: <Link to="/patient-suche">Patient suchen</Link>,
  };

  const arbeitsbereich: ItemType[] = [
    {
      key: 'dashboard',
      icon: <DashboardOutlined />,
      label: <Link to="/dashboard">Dashboard</Link>,
    },
    {
      key: 'offene-anforderungen',
      className: 'menu-item-count',
      // collapsed flyout renders in a portal (no menu CSS); drive its text via `title`
      title: openCount !== undefined ? `Offene (${openCount})` : 'Offene',
      icon: <AuditOutlined />,
      label: (
        <Link to="/offene-anforderungen" className="menu-count-link">
          <span>Offene</span>
          {openCount !== undefined && <span className="menu-count">{openCount}</span>}
        </Link>
      ),
    },
    {
      key: 'gesendete-anforderungen',
      icon: <SendOutlined />,
      label: <Link to="/gesendete-anforderungen">Gesendete</Link>,
    },
    ...(aisSession ? [] : [patientSuche]),
  ];

  const labor: ItemType[] = [];

  if (hasSomeRole([Role.ROLE_LR_MEDCOM, Role.ROLE_LR_LAB_STAFF], auth.user)) {
    labor.push({
      key: 'probenannahme',
      icon: <ExperimentOutlined />,
      label: <Link to="/probenannahme">Probenannahme</Link>,
    });
  }

  if (isFrontDesk || hasSomeRole([Role.ROLE_LR_MEDCOM], auth.user)) {
    labor.push(
      {
        key: 'zuweiserauswahl',
        icon: <SwapOutlined />,
        label: <Link to="/zuweiserauswahl">Zuweiser auswählen</Link>,
      },
      {
        key: 'alle-offenen-pv-anforderungen',
        icon: <DatabaseOutlined />,
        label: <Link to="/alle-offenen-pv-anforderungen">Alle offenen</Link>,
      }
    );
  }

  if (hasSomeRole([Role.ROLE_LR_MEDCOM, Role.ROLE_LR_LAB_ADMIN, Role.ROLE_LR_FRONT_DESK], auth.user)) {
    labor.push({
      key: 'fibu-export',
      icon: <CloudDownloadOutlined />,
      label: <Link to="/fibu-export">Fibu-Export</Link>,
    });
  }

  const verwaltung: ItemType[] = [];

  if (hasSomeRole([Role.ROLE_LR_MEDCOM, Role.ROLE_LR_LAB_ADMIN], auth.user)) {
    verwaltung.push({
      key: 'labadmin',
      icon: <ExperimentOutlined />,
      label: <Link to="/labadmin">Labor</Link>,
    });
  }

  if (hasSomeRole([Role.ROLE_LR_MEDCOM, Role.ROLE_LR_LAB_ADMIN, Role.ROLE_LR_USER, Role.ROLE_LR_FRONT_DESK], auth.user)) {
    const settings: ItemType[] = [];
    if (hasSomeRole([Role.ROLE_LR_MEDCOM, Role.ROLE_LR_LAB_ADMIN, Role.ROLE_LR_USER], auth.user)) {
      settings.push({
        key: 'einstellungen/zuweiser',
        icon: <TeamOutlined />,
        label: <Link to="/einstellungen/zuweiser">Zuweiser</Link>,
      });
    }

    settings.push(
      {
        key: 'einstellungen/profile',
        icon: <ProfileOutlined />,
        label: <Link to="/einstellungen/profile">Profile</Link>,
      },
      {
        key: 'einstellungen/sheets',
        icon: <FileDoneOutlined />,
        label: <Link to="/einstellungen/sheets">Sheets</Link>,
      }
    );

    if (!aisSession) {
      settings.push({
        key: 'einstellungen/ecard',
        icon: <IdcardOutlined />,
        label: <Link to="/einstellungen/ecard">e-card</Link>,
      });
    }

    if (settings.length) {
      verwaltung.push({
        key: 'einstellungen',
        icon: <ToolOutlined />,
        label: 'Einstellungen',
        children: settings,
      });
    }
  }

  const adminChildren: ItemType[] = [];

  if (hasSomeRole([Role.ROLE_LR_MEDCOM], auth.user)) {
    adminChildren.push(
      {
        key: 'admin/labors',
        icon: <ExperimentOutlined />,
        label: <Link to="/admin/labors">Labors</Link>,
      },
      {
        key: 'admin/krankenkassen',
        icon: <MedicineBoxOutlined />,
        label: <Link to="/admin/krankenkassen">Krankenkassen</Link>,
      },
      {
        key: 'admin/themes',
        icon: <DesktopOutlined />,
        label: <Link to="/admin/themes">Themes</Link>,
      },
      {
        key: 'admin/zuweiser',
        icon: <TeamOutlined />,
        label: <Link to="/admin/zuweiser">Zuweiser</Link>,
      },
      {
        key: 'admin/benutzer',
        icon: <UserOutlined />,
        label: <Link to="/admin/benutzer">Benutzer</Link>,
      },
      {
        key: 'admin/db-tool-import',
        icon: <CloudUploadOutlined />,
        label: <Link to="/admin/db-tool-import">DBTool Import</Link>,
      },
      {
        key: 'admin/system-status',
        icon: <NotificationOutlined />,
        label: <Link to="/admin/system-status">System Status</Link>,
      },
      {
        key: 'admin/ai-konfiguration',
        icon: <AiSparklesIcon size={16} />,
        label: <Link to="/admin/ai-konfiguration">KI-Konfiguration</Link>,
      }
    );
  }

  if (adminChildren.length) {
    verwaltung.push({
      key: 'admin',
      icon: <SettingOutlined />,
      label: 'Administration',
      children: adminChildren,
    });
  }

  const items: ItemType[] = [...(launchOnly ? [] : [neueAnforderung]), ...arbeitsbereich];
  if (labor.length) {
    items.push({ type: 'group', label: 'Labor', children: labor });
  }
  if (verwaltung.length) {
    items.push({ type: 'group', label: 'Verwaltung', children: verwaltung });
  }

  const menuClass = css`
    border: none !important;
    padding: 0 8px;

    .ant-menu-item,
    .ant-menu-submenu-title {
      border-radius: ${token.borderRadius}px;
    }

    .ant-menu-item-group-title {
      font-size: 10.5px;
      font-weight: 700;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      color: ${token.colorTextTertiary};
      padding-top: ${token.marginSM}px;
      padding-bottom: ${token.marginXXS}px;
    }

    /* active fill aligned to the primary color (cleaner than AntD's muted colorPrimaryBg) */
    .ant-menu-item-selected:not(.menu-cta) {
      background: color-mix(in srgb, ${token.colorPrimary} 8%, transparent) !important;
    }

    /* accent bar on the active item */
    .ant-menu-item-selected::before {
      content: '';
      position: absolute;
      inset-inline-start: 0;
      top: 8px;
      bottom: 8px;
      width: 3px;
      border-radius: 0 3px 3px 0;
      background: ${token.colorPrimary};
    }

    /* open-requests count badge */
    .menu-item-count .ant-menu-title-content {
      flex: 1;
    }
    .menu-count-link {
      display: flex;
      align-items: center;
      width: 100%;
    }
    .menu-count {
      margin-left: auto;
      min-width: 20px;
      padding: 0 6px;
      border-radius: 10px;
      font-size: 11px;
      font-weight: 600;
      line-height: 18px;
      text-align: center;
      font-variant-numeric: tabular-nums;
      background: ${token.colorFillSecondary};
      color: ${token.colorTextSecondary};
    }

    /* prominent "Neue Anforderung" — solid primary button */
    .menu-cta {
      margin-top: ${token.marginLG}px;
      margin-bottom: ${token.marginSM}px;
      font-weight: 500;
      background: transparent !important;
      border: 1px solid color-mix(in srgb, ${token.colorPrimary} 25%, transparent);
      box-shadow: 0 1px 2px color-mix(in srgb, ${token.colorPrimary} 20%, transparent);
    }
    .menu-cta,
    .menu-cta a,
    .menu-cta .anticon {
      color: ${token.colorPrimary} !important;
    }
    .menu-cta:hover {
      background: color-mix(in srgb, ${token.colorPrimary} 8%, transparent) !important;
    }
    .menu-cta:active {
      background: color-mix(in srgb, ${token.colorPrimary} 15%, transparent) !important;
      box-shadow: none;
      transform: translateY(1px);
    }
    /* keep the accent bar off the CTA */
    .menu-cta.ant-menu-item-selected::before {
      display: none;
    }
  `;

  return (
    <ConfigProvider
      theme={{
        components: {
          Menu: {
            itemColor: token.colorTextSecondary,
            itemHoverColor: token.colorPrimary,
            groupTitleColor: token.colorTextTertiary,
            itemActiveBg: `color-mix(in srgb, ${token.colorPrimary} 8%, transparent)`,
          },
        },
      }}
    >
      <Menu
        items={items}
        mode="inline"
        selectedKeys={level2 && !hasSubmenuItemsWithoutSubmenu ? [`${level1}/${level2}`] : [level1]}
        defaultOpenKeys={[level1]}
        rootClassName={menuClass}
      />
    </ConfigProvider>
  );
};
